import { FC } from 'react';
import { useNavigate } from 'react-router-dom';
import { ProactiveInsight } from '../hooks/useProactiveInsights';

interface InsightBannerProps {
  insight: ProactiveInsight;
  onDismiss: (id: string) => void;
  onNavigate?: () => void;
}

const priorityStyles = {
  high: {
    container:
      'bg-gradient-to-br from-red-50 to-orange-50 dark:from-red-900/20 dark:to-orange-900/10 border-red-200 dark:border-red-700/40',
    iconBg: 'bg-gradient-to-br from-red-100 to-orange-100 dark:from-red-800/40 dark:to-orange-800/30',
    icon: 'text-red-500 dark:text-red-400',
    action: 'text-red-600 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300',
  },
  medium: {
    container:
      'bg-gradient-to-br from-amber-50 to-yellow-50 dark:from-amber-900/20 dark:to-yellow-900/10 border-amber-200 dark:border-amber-700/40',
    iconBg: 'bg-gradient-to-br from-amber-100 to-yellow-100 dark:from-amber-800/40 dark:to-yellow-800/30',
    icon: 'text-amber-500 dark:text-amber-400',
    action: 'text-amber-600 dark:text-amber-400 hover:text-amber-700 dark:hover:text-amber-300',
  },
  low: {
    container:
      'bg-gradient-to-br from-green-50 to-emerald-50 dark:from-green-900/20 dark:to-emerald-900/10 border-green-200 dark:border-green-700/40',
    iconBg: 'bg-gradient-to-br from-green-100 to-emerald-100 dark:from-green-800/40 dark:to-emerald-800/30',
    icon: 'text-green-500 dark:text-green-400',
    action: 'text-green-600 dark:text-green-400 hover:text-green-700 dark:hover:text-green-300',
  },
};

const priorityIcons = {
  high: (
    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
      />
    </svg>
  ),
  medium: (
    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
      />
    </svg>
  ),
  low: (
    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
      />
    </svg>
  ),
};

const InsightBanner: FC<InsightBannerProps> = ({ insight, onDismiss, onNavigate }) => {
  const navigate = useNavigate();
  const styles = priorityStyles[insight.priority];

  const handleAction = () => {
    if (insight.suggestedAction?.href) {
      navigate(insight.suggestedAction.href);
      onNavigate?.();
    }
  };

  return (
    <div
      className={`p-3.5 rounded-xl border ${styles.container} mb-3 shadow-sm transition-all duration-200 hover:shadow-md`}
    >
      <div className="flex items-start gap-3">
        <div
          className={`flex-shrink-0 w-8 h-8 rounded-lg ${styles.iconBg} flex items-center justify-center ${styles.icon}`}
        >
          {priorityIcons[insight.priority]}
        </div>
        <div className="flex-1 min-w-0">
          <h4 className="font-semibold text-sm text-gray-900 dark:text-white">{insight.title}</h4>
          <p className="text-xs text-gray-600 dark:text-gray-400 mt-1 leading-relaxed line-clamp-2">
            {insight.description}
          </p>
          {insight.suggestedAction && (
            <button
              onClick={handleAction}
              className={`text-xs ${styles.action} font-semibold mt-2 flex items-center gap-1 transition-colors`}
            >
              {insight.suggestedAction.label}
              <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M9 5l7 7-7 7" />
              </svg>
            </button>
          )}
        </div>
        <button
          onClick={() => onDismiss(insight.id)}
          className="flex-shrink-0 p-1.5 hover:bg-gray-200/50 dark:hover:bg-slate-700/50 rounded-lg transition-colors"
          aria-label="Dismiss insight"
        >
          <svg
            className="w-4 h-4 text-gray-400 dark:text-gray-500"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>
    </div>
  );
};

export default InsightBanner;
